import type { ChangeEvent, ComponentType, ReactNode } from 'react';
import { StarIcon } from '../../../../assets/icons/StarIcon';
import { PlusIcon } from '../../../../assets/icons/PlusIcon';
import { CloseIcon } from '../../../../assets/icons/CloseIcon';
import LoggingClick from '../../../../utils/logger/components/LoggingClick';
import Select from './Select';
import ProductImage from './ProductImage/ProductImage';
import {
  Section,
  Label,
  StarRow,
  Star,
  Input,
  TextArea,
  FileInput,
  Row,
  BottomButtonRow,
  SubmitButton,
  GuideText,
  RadioGroup,
  RadioLabelWrapper,
  RadioLabel,
  Radio,
  TermsBox,
  TermsTitle,
  TermsText,
  LabelWrapper,
  IntroSection,
  TextContainer,
  IntroText,
  IntroSubText,
  ImageContainer,
  ImageUploadBox,
  Thumbnail,
  ThumbnailImage,
  RequiredIndicator,
  RemoveButton,
  UploadArea,
  SmallUploadArea,
  Optional,
} from './SharedStyles';

interface SectionProps {
  children: ReactNode;
  style?: React.CSSProperties;
}

interface RenderProductIntroProps {
  productImage: string;
}

interface RenderRatingSectionProps {
  rating: number;
  setRating: (rating: number) => void;
  SectionComponent?: ComponentType<SectionProps>;
}

interface RenderSkinInfoSectionProps {
  skinType: string;
  setSkinType: (value: string) => void;
  skinConcern: string;
  setSkinConcern: (value: string) => void;
  skinTypeOptions: Array<{ value: string; label: string }>;
  skinConcernOptions: Array<{ value: string; label: string }>;
  SectionComponent?: ComponentType<SectionProps>;
}

interface RenderReviewContentSectionProps {
  content: string;
  setContent: (content: string) => void;
  SectionComponent?: ComponentType<SectionProps>;
}

interface RenderTitleSectionProps {
  title: string;
  setTitle: (title: string) => void;
  SectionComponent?: ComponentType<SectionProps>;
}

interface RenderImageUploadSectionProps {
  images: File[];
  imagePreviews: string[];
  handleImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: (idx: number) => void;
  SectionComponent?: ComponentType<SectionProps>;
}

interface RenderRecommendationSectionProps {
  recommend: string;
  setRecommend: (value: string) => void;
  SectionComponent?: ComponentType<SectionProps>;
}

interface RenderNicknameSectionProps {
  nickname: string;
  setNickname: (nickname: string) => void;
  SectionComponent?: ComponentType<SectionProps>;
}

interface RenderTermsSectionProps {
  SectionComponent?: ComponentType<SectionProps>;
}

interface RenderSubmitSectionProps {
  isSubmitDisabled: boolean;
  onSubmit: () => void;
  productId?: string | number;
}

export const renderProductIntro = ({ productImage }: RenderProductIntroProps) => (
  <IntroSection>
    <TextContainer>
      <IntroText>Tell others what you think and you could win 20% on your next purchase</IntroText>
      <IntroSubText>One winner daily. The more reviews you write, the better your odds.</IntroSubText>
    </TextContainer>
    <ImageContainer>
      <ProductImage src={productImage} alt="Product" />
    </ImageContainer>
  </IntroSection>
);

export const renderRatingSection = ({ rating, setRating, SectionComponent = Section }: RenderRatingSectionProps) => (
  <SectionComponent>
    <Label>
      Overall rating <RequiredIndicator>*</RequiredIndicator>
    </Label>
    <StarRow>
      {[1, 2, 3, 4, 5].map((num) => (
        <Star
          key={num}
          type="button"
          selected={rating >= num}
          onClick={() => setRating(num)}
          aria-label={`Rating ${num} stars`}
        >
          <StarIcon filled={rating >= num} />
        </Star>
      ))}
    </StarRow>
  </SectionComponent>
);

export const renderSkinInfoSection = ({
  skinType,
  setSkinType,
  skinConcern,
  setSkinConcern,
  skinTypeOptions,
  skinConcernOptions,
  SectionComponent = Section,
}: RenderSkinInfoSectionProps) => (
  <SectionComponent>
    <Label>
      More about you <RequiredIndicator>*</RequiredIndicator>
    </Label>
    <Row>
      <Section style={{ flex: 1, marginBottom: 0 }}>
        <Select options={skinTypeOptions} value={skinType} onChange={setSkinType} title="Skin type" showSelectedValue />
      </Section>
      <Section style={{ flex: 1, marginBottom: 0 }}>
        <Select
          options={skinConcernOptions}
          value={skinConcern}
          onChange={setSkinConcern}
          title="Skin concern"
          showSelectedValue
        />
      </Section>
    </Row>
  </SectionComponent>
);

export const renderReviewContentSection = ({
  content,
  setContent,
  SectionComponent = Section,
}: RenderReviewContentSectionProps) => (
  <SectionComponent>
    <Label htmlFor="review-content">
      Your review <RequiredIndicator>*</RequiredIndicator>
    </Label>
    <TextArea id="review-content" maxLength={5000} value={content} onChange={(e) => setContent(e.target.value)} />
    <GuideText>• 5000 characters max</GuideText>
  </SectionComponent>
);

export const renderTitleSection = ({ title, setTitle, SectionComponent = Section }: RenderTitleSectionProps) => (
  <SectionComponent>
    <Label htmlFor="review-title">
      Add a title <RequiredIndicator>*</RequiredIndicator>
    </Label>
    <Input id="review-title" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
    <GuideText>• 200 characters max</GuideText>
  </SectionComponent>
);

export const renderImageUploadSection = ({
  images,
  imagePreviews,
  handleImageChange,
  handleRemoveImage,
  SectionComponent = Section,
}: RenderImageUploadSectionProps) => (
  <SectionComponent>
    <LabelWrapper>
      <Label>Add photos</Label>
      <Optional>Optional</Optional>
    </LabelWrapper>
    <ImageUploadBox>
      {images.map((file, idx) => (
        <Thumbnail key={`${file.name}-${file.size}-${file.lastModified}`}>
          <ThumbnailImage src={imagePreviews[idx]} alt={`Review photo ${idx + 1}`} />
          <RemoveButton type="button" onClick={() => handleRemoveImage(idx)} aria-label="Remove photo">
            <CloseIcon />
          </RemoveButton>
        </Thumbnail>
      ))}
      {images.length < 5 &&
        (images.length === 0 ? (
          <UploadArea htmlFor="review-image">
            <PlusIcon />
            <FileInput id="review-image" type="file" accept="image/*" multiple onChange={handleImageChange} />
          </UploadArea>
        ) : (
          <SmallUploadArea htmlFor="review-image">
            <PlusIcon />
            <FileInput id="review-image" type="file" accept="image/*" multiple onChange={handleImageChange} />
          </SmallUploadArea>
        ))}
    </ImageUploadBox>
  </SectionComponent>
);

export const renderRecommendationSection = ({
  recommend,
  setRecommend,
  SectionComponent = Section,
}: RenderRecommendationSectionProps) => (
  <SectionComponent>
    <LabelWrapper>
      <Label>Would you recommend this item</Label>
    </LabelWrapper>
    <RadioLabelWrapper>
      <RadioGroup>
        <RadioLabel>
          <Radio
            type="radio"
            name="recommend"
            value="yes"
            checked={recommend === 'yes'}
            onChange={() => setRecommend('yes')}
          />
          Yes
        </RadioLabel>
        <RadioLabel>
          <Radio
            type="radio"
            name="recommend"
            value="no"
            checked={recommend === 'no'}
            onChange={() => setRecommend('no')}
          />
          No
        </RadioLabel>
      </RadioGroup>
    </RadioLabelWrapper>
  </SectionComponent>
);

export const renderNicknameSection = ({
  nickname,
  setNickname,
  SectionComponent = Section,
}: RenderNicknameSectionProps) => (
  <SectionComponent>
    <Label htmlFor="review-nickname">
      Create a display name <RequiredIndicator>*</RequiredIndicator>
    </Label>
    <Input id="review-nickname" maxLength={25} value={nickname} onChange={(e) => setNickname(e.target.value)} />
    <GuideText>• Must be 4 to 25 characters.</GuideText>
    <GuideText>• Please don't use spaces, special characters or emojis</GuideText>
  </SectionComponent>
);

export const renderTermsSection = ({ SectionComponent = Section }: RenderTermsSectionProps) => (
  <SectionComponent>
    <TermsBox>
      <TermsTitle>Terms & Conditions</TermsTitle>
      <TermsText>
        We will email you to notify you that your review had been posted or if someone replies to your review.
      </TermsText>
    </TermsBox>
  </SectionComponent>
);

export const renderSubmitSection = ({ isSubmitDisabled, onSubmit, productId }: RenderSubmitSectionProps) => (
  <BottomButtonRow>
    <LoggingClick logName="submit_review_button" params={{ productId: productId?.toString(), action: 'submit_review' }}>
      <SubmitButton type="button" disabled={isSubmitDisabled} onClick={onSubmit}>
        Submit review
      </SubmitButton>
    </LoggingClick>
  </BottomButtonRow>
);
